import type { PageFlagKey } from '@/shared/types/passbook';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http, legacy } from '@/shared/api/client';
import type {
  AiGlobalSettings,
  AiModelCatalog,
  AiSettingsPatch,
  AiUserRow,
  PagedResult,
} from '../model/types';

interface Envelope<T> {
  data: T;
}

function toQuery(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export interface CreditsOverview {
  month: string;
  totals: {
    costUsd: number;
    inputTokens: number;
    outputTokens: number;
    calls: number;
    sessions: number;
    /** Segmentações distintas salvas a partir das conversas do mês. */
    segmentations: number;
    activeUsers: number;
    nearLimitUsers: number;
    blockedUsers: number;
  };
  byModel: Array<{ model: string } & CostBreakdown>;
  byPassbook: Array<{ idPassbook: string; name: string } & CostBreakdown>;
}

export interface CostBreakdown {
  costUsd: number;
  calls: number;
  /** Conversas com o assistente. */
  sessions: number;
  /** Segmentações de fato criadas nessas conversas. */
  segmentations: number;
}

export type BreakdownGroupBy = 'model' | 'passbook' | 'user';
export type BreakdownSort = 'costUsd' | 'segmentations' | 'sessions' | 'costPerSegmentation';

export interface BreakdownParams {
  month: string;
  groupBy: BreakdownGroupBy;
  sort: BreakdownSort;
  page: number;
  limit: number;
  search?: string;
  model?: string;
  idPassbook?: string;
  /** Só conversas que salvaram (`with`) ou não salvaram (`without`) segmentação. */
  segmentation?: 'with' | 'without';
}

export interface BreakdownRow extends CostBreakdown {
  /** Id do modelo, passbook ou usuário. */
  key: string;
  label: string;
  sublabel?: string;
}

export interface BreakdownResult {
  rows: BreakdownRow[];
  total: number;
  /** Totais de tudo que passa pelos filtros, não só da página. */
  summary: CostBreakdown;
}

export interface UsageRow {
  id: string;
  user: { id: string; name: string };
  passbook: { id: string; name: string };
  segmentation: { id: string; name: string } | null;
  model: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  calls: number;
  aborted: boolean;
  startedAt: string;
  endedAt: string;
}

export interface UsageFilter {
  userId?: string;
  segmentationId?: string;
  idPassbook?: string;
}

export const aiAdminKeys = {
  all: ['ai-admin'] as const,
  models: ['ai-admin', 'models'] as const,
  global: ['ai-admin', 'global'] as const,
  overview: (month: string) => ['ai-admin', 'overview', month] as const,
  breakdown: (params: BreakdownParams) => ['ai-admin', 'breakdown', params] as const,
  users: ['ai-admin', 'users'] as const,
  usage: (filter: UsageFilter, page: number) => ['ai-admin', 'usage', filter, page] as const,
};

/** Modelos GPT-5.6 com preço do catálogo público do OpenRouter (BFF, cache de 1h no servidor). */
export function useAiModels() {
  return useQuery({
    queryKey: aiAdminKeys.models,
    queryFn: async () => (await http.get<Envelope<AiModelCatalog>>('/bff/ai/models')).data,
    staleTime: 30 * 60_000,
  });
}

export function useAiGlobalSettings() {
  return useQuery({
    queryKey: aiAdminKeys.global,
    queryFn: async () =>
      (await legacy.get<Envelope<AiGlobalSettings>>('/admin/ai-credits/global')).data,
  });
}

export function useSaveAiGlobalSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AiGlobalSettings & { clearUserLimits?: boolean }) =>
      (
        await legacy.put<Envelope<{ settings: AiGlobalSettings; clearedUserLimits: number }>>(
          '/admin/ai-credits/global',
          input,
        )
      ).data,
    onSuccess: (result) => {
      queryClient.setQueryData(aiAdminKeys.global, result.settings);
      // Tudo que mostra valor efetivo (herdado do global) fica desatualizado.
      void queryClient.invalidateQueries({ queryKey: aiAdminKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['permissions'] });
    },
  });
}

export function useCreditsOverview(month: string) {
  return useQuery({
    queryKey: aiAdminKeys.overview(month),
    queryFn: async () =>
      (
        await legacy.get<Envelope<CreditsOverview>>(
          `/admin/ai-credits/overview${toQuery({ month })}`,
        )
      ).data,
    placeholderData: keepPreviousData,
  });
}

export function useCostBreakdown(params: BreakdownParams) {
  return useQuery({
    queryKey: aiAdminKeys.breakdown(params),
    queryFn: async () =>
      (
        await legacy.get<Envelope<BreakdownResult>>(
          `/admin/ai-credits/breakdown${toQuery({ ...params })}`,
        )
      ).data,
    placeholderData: keepPreviousData,
  });
}

export interface CreditUsersParams {
  page: number;
  limit: number;
  search?: string;
  idPassbook?: string;
}

export function useCreditUsers(params: CreditUsersParams) {
  return useQuery({
    queryKey: [...aiAdminKeys.users, params],
    queryFn: async () =>
      (
        await legacy.get<Envelope<PagedResult<AiUserRow>>>(
          `/admin/ai-credits/users${toQuery({ ...params })}`,
        )
      ).data,
    placeholderData: keepPreviousData,
  });
}

export function useUsage(filter: UsageFilter, page: number, enabled = true) {
  return useQuery({
    queryKey: aiAdminKeys.usage(filter, page),
    queryFn: async () =>
      (
        await legacy.get<Envelope<PagedResult<UsageRow>>>(
          `/admin/ai-credits/usage${toQuery({ ...filter, page, limit: 20 })}`,
        )
      ).data,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useBulkSetLimit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userIds: string[]; monthlyLimitUsd: number | null }) =>
      (await legacy.put<Envelope<{ modified: number }>>('/admin/ai-credits/bulk', input)).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: aiAdminKeys.all }),
  });
}

/** `idPassbook` define o contexto do valor efetivo devolvido (loja/passbook daquele usuário). */
export function useUpdateAiUser(idPassbook?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: {
      id: string;
      active?: boolean;
      hasCampaignAdvanced?: boolean;
      name?: string;
      email?: string;
      password?: string;
      role?: string;
      stores?: string[];
      aiSettings?: AiSettingsPatch;
      /** `null` volta a seguir o passbook. */
      pageFlags?: Partial<Record<PageFlagKey, boolean | null>>;
    }) =>
      (
        await legacy.put<Envelope<AiUserRow>>(`/admin/permissions/users/${id}`, {
          ...input,
          idPassbook,
        })
      ).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aiAdminKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['permissions', 'users'] });
    },
  });
}

export function useResetUserUsage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) =>
      (await legacy.post<Envelope<AiUserRow>>(`/admin/ai-credits/users/${userId}/reset`)).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: aiAdminKeys.all }),
  });
}
